import { array } from '@ember/helper';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { sanitize } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

export interface Args {
	text?: string | JQuery<HTMLElement>;
	interactive?: boolean;
	showOnlyIfTruncated?: boolean;
	extraClass?: string;
	delay?: number;
	maxWidth?: number;
	position?: string;
	triggerFunc?: string;
	timer?: number;
	theme?: string | null;
	onShowGetText?: () => Promise<string>;
	onClose?: () => void;
}

export default class ToolTipComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;

	private tooltip: TooltipsterInstance | null = null;

	get themeClass() {
		if (this.args.theme) {
			return `tooltipster-theme-${this.args.theme}`;
		}
		return '';
	}

	get triggerFuncName() {
		const defaultTrigger = this.navigationService.isMobileApp ? 'custom' : 'hover';
		return this.args.triggerFunc || defaultTrigger;
	}

	@action
	setupView(node: HTMLSpanElement) {
		schedule('afterRender', () => {
			this.initializeTooltip(node);
		});
	}

	@action
	teardownView() {
		this.destroyTooltip();
	}

	initializeTooltip(node: HTMLSpanElement) {
		const el = $(node),
			text = this.getSafeText(this.args.text);

		this.destroyTooltip();

		let width = this.args.maxWidth;
		if (!width) {
			// default width depending on how long the text is but no less than 140
			width = text.length > 150 ? 300 : 140;
		}

		const parent = el ? el.parent() : null;

		if (parent?.length && (text || this.args.onShowGetText)) {
			const options: Record<string, unknown> = {
				contentAsHTML: true,
				content: text || '...',
				theme: `tooltipster-default ${this.themeClass || ''} ${this.args.extraClass || ''}`,
				delay: this.args.delay || 500,
				maxWidth: width,
				// offsetX: (+this.get('offset-x')),
				// offsetY: (+this.get('offset-y')),
				position: this.args.position || 'bottom',
				trigger: this.triggerFuncName,
				timer: this.args.timer || 0,
				animationDuration: 200,
				interactive: this.args.interactive,
				functionBefore: (
					_instance: TooltipsterInstance,
					helper: { origin: HTMLElement },
				) => {
					const helperEl = $(helper.origin);
					if (
						this.args.showOnlyIfTruncated &&
						helper.origin.scrollWidth <= helper.origin.clientWidth
					) {
						return false;
					}
					if (el.is(':hidden')) {
						// don't show when hidden - usually with responsive css
						return false;
					}
					if (
						helperEl.hasClass('ui-draggable-handle') &&
						helperEl.closest('.ui-draggable').hasClass('ui-draggable-dragging')
					) {
						// don't show tooltips when dragging
						return false;
					}

					if (this.args.onShowGetText) {
						void this.args.onShowGetText().then((text) => {
							this.setSafeText(text);
						});
					} else {
						this.setSafeText(this.args.text);
					}

					return true;
				},
				functionAfter: () => {
					if (this.args.onClose) {
						this.args.onClose();
					}
				},
			};

			// Handling tooltips on mobile app with custom trigger
			if (this.triggerFuncName === 'custom' && this.navigationService.isMobileApp) {
				options.triggerOpen = {
					tap: true,
				};

				options.triggerClose = {
					tap: true,
					scroll: true,
				};
			}

			parent.tooltipster(options);

			this.tooltip = parent.tooltipster('instance');
		}
	}

	private setSafeText(text?: string | JQuery<HTMLElement>) {
		try {
			const safeText = this.getSafeText(text);
			this.tooltip?.content(safeText);
		} catch (_e) {
			// skip
		}
	}

	private getSafeText(text?: string | JQuery<HTMLElement>) {
		if (text) {
			return text instanceof $ ? text : sanitize(text as string);
		}
		return '';
	}

	destroyTooltip() {
		if (this.tooltip) {
			try {
				this.tooltip.destroy();
				this.tooltip = null;
			} catch (_e) {
				// skip
			}
		}
	}
	<template>
		<span
			class={{joinClasses (array "faketooltipcomponent" this.themeClass @extraClass)}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
		</span>
	</template>
}
